import {
  Component,
  OnDestroy,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

@Component({
  selector: 'app-maintenance',
  templateUrl: './maintenance.component.html',
  styleUrls: ['./maintenance.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MaintenanceComponent implements AfterViewInit, OnDestroy {
  showVideoIntro = true;
  isMuted = true;

  private readonly videoId = 'be4CMSdKrRo';
  private readonly startSeconds = 0;
  private readonly playerElementId = 'maintenance-yt-player';
  private player: any = null;

  ngAfterViewInit(): void {
    if (this.showVideoIntro) {
      this.initPlayer();
    }
  }

  ngOnDestroy(): void {
    this.destroyPlayer();
  }

  closeVideo(): void {
    this.destroyPlayer();
    this.showVideoIntro = false;
  }

  openVideo(): void {
    this.showVideoIntro = true;
    queueMicrotask(() => this.initPlayer());
  }

  /**
   * Browsers block autoplay-with-sound until a user gesture.
   * Video starts muted so it autoplays immediately; user can unmute via button.
   */
  onOverlayClick(_event: Event): void {
    // overlay click no longer needed for sound — button handles it
  }

  toggleMute(): void {
    if (!this.player) return;
    try {
      if (this.isMuted) {
        this.player.unMute();
        this.player.setVolume(100);
        this.isMuted = false;
      } else {
        this.player.mute();
        this.isMuted = true;
      }
    } catch {
      // ignore
    }
  }

  private initPlayer(): void {
    this.loadYouTubeApi().then(() => this.createPlayer());
  }

  private loadYouTubeApi(): Promise<void> {
    return new Promise((resolve) => {
      if (window.YT?.Player) {
        resolve();
        return;
      }

      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        previous?.();
        resolve();
      };

      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(script);
      }
    });
  }

  private createPlayer(): void {
    if (!this.showVideoIntro || !window.YT?.Player) {
      return;
    }

    if (!document.getElementById(this.playerElementId)) {
      return;
    }

    if (this.player?.destroy) {
      this.player.destroy();
      this.player = null;
    }

    this.player = new window.YT.Player(this.playerElementId, {
      videoId: this.videoId,
      width: '100%',
      height: '100%',
      playerVars: {
        autoplay: 1,
        mute: 1,
        loop: 1,
        playlist: this.videoId,
        start: this.startSeconds,
        controls: 0,
        disablekb: 1,
        cc_load_policy: 0,
        iv_load_policy: 3,
        modestbranding: 1,
        rel: 0,
        playsinline: 1,
        fs: 0,
      },
      events: {
        onReady: (event: any) => this.onPlayerReady(event),
        onStateChange: (event: any) => this.onPlayerStateChange(event),
      },
    });
  }

  private onPlayerReady(event: any): void {
    const player = event.target;
    this.disableCaptions();
    try {
      // Already muted via playerVars — just start playback
      player.playVideo();
    } catch {
      // ignore
    }
  }

  private onPlayerStateChange(event: any): void {
    const state = event?.data;

    if (state === 1 /* PLAYING */) {
      this.disableCaptions();
      return;
    }

    // Keep the intro playing — avoids YouTube's large pause/play overlay.
    if (state === 2 /* PAUSED */ && this.showVideoIntro) {
      try {
        this.player?.playVideo();
      } catch {
        // ignore
      }
    }
  }

  private playWithSound(): void {
    if (!this.player) {
      return;
    }
    try {
      this.player.unMute();
      this.player.setVolume(100);
      this.player.playVideo();
      this.disableCaptions();
    } catch {
      // ignore
    }
  }

  private disableCaptions(): void {
    if (!this.player) {
      return;
    }
    try {
      this.player.setOption?.('captions', 'track', {});
      this.player.unloadModule?.('captions');
      this.player.unloadModule?.('cc');
    } catch {
      // best-effort
    }
  }

  private destroyPlayer(): void {
    if (this.player?.destroy) {
      try {
        this.player.destroy();
      } catch {
        // ignore
      }
      this.player = null;
    }
  }
}
